import { useQuery } from '@tanstack/react-query';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchMedicalProfessionalPatients } from '~/services/medical-professionals';
import { formatDateIfValid } from '~/utils';
import { Pagination } from '../Pagination';

interface VerifiedPatient {
  id: number;
  name: string;
  hipaa: boolean;
  status: string;
  verifyExpensesStatus: string;
  verifyExpensesDate: Date;
  verifyExpensesMedicalProfessional: string;
  verifierName: string;
  emailed: Date;
  hippa: boolean;
  campaign: number;
  patient_id?: number;
}

const columnHelper = createColumnHelper<VerifiedPatient>();

// `clientLinkMode` controls where the Client name link in each row goes.
// - 'campaign' (default): /campaigns/:campaign_id — the staff/outreach
//   view used on /medical-professionals/:id.
// - 'verify': /verify/:patientId — the med-pro-facing verify page used
//   on the med-pro's own dashboard.
type ClientLinkMode = 'campaign' | 'verify';

interface Props {
  id: number;
  clientLinkMode?: ClientLinkMode;
}

const buildColumns = (clientLinkMode: ClientLinkMode) => {
  const isVerifyMode = clientLinkMode === 'verify';
  const cols: any[] = [
    columnHelper.accessor('name', {
      id: 'name',
      header: () => 'Client',
      cell: (info) => {
        const row = info?.row?.original;
        const target = isVerifyMode
          ? row?.patient_id
            ? `/verify/${row.patient_id}`
            : '#'
          : row?.campaign
          ? `/campaigns/${row.campaign}`
          : '#';
        return (
          <Link className='font-bold text-primary' to={target}>
            {info.renderValue()}
          </Link>
        );
      },
    }),
    columnHelper.accessor('status', {
      header: 'Status',
      cell: (info) => (
        <div
          className={
            'inline-flex rounded-full bg-[#AFC12D] px-3 py-1 text-sm hover:bg-opacity-90'
          }
        >
          {info.renderValue()}
        </div>
      ),
    }),
    columnHelper.accessor('verifyExpensesStatus', {
      header: 'Verify Expenses',
      // Med-pro view: pill only — date and verifier moved into their own
      // columns ("Verified" / "Verified by") on the right.
      // Staff view: existing pill + "MM/DD/YY by Name" sub-line.
      cell: (info) =>
        isVerifyMode ? (
          <div className='inline-flex rounded-full bg-[#01AEB4] px-3 py-1 text-sm hover:bg-opacity-90'>
            {info.renderValue()}
          </div>
        ) : (
          <div>
            <div className='mb-[3px] inline-flex rounded-full bg-[#01AEB4] px-3 py-1 text-sm hover:bg-opacity-90'>
              {info.renderValue()}
            </div>
            <div className='text-[12px]/[16px]'>
              {info.row.original.verifyExpensesDate &&
                formatDateIfValid(
                  info.row.original.verifyExpensesDate,
                  'MM/dd/yy'
                )}{' '}
              by {info.row.original.verifyExpensesMedicalProfessional}
            </div>
          </div>
        ),
    }),
  ];

  if (isVerifyMode) {
    cols.push(
      columnHelper.accessor('verifyExpensesDate', {
        header: 'Verified',
        cell: (info) =>
          info.row.original.verifyExpensesDate
            ? formatDateIfValid(
                info.row.original.verifyExpensesDate,
                'MM/dd/yy'
              )
            : '—',
      }),
      columnHelper.accessor('verifierName', {
        header: 'Verified by',
        cell: (info) => info.row.original.verifierName || '—',
      })
    );
  } else {
    cols.push(
      columnHelper.accessor('emailed', {
        header: 'Emailed',
        cell: (info) =>
          formatDateIfValid(info.row.original.emailed, 'MM/dd/yy'),
      })
    );
  }

  return cols;
};

const VerifiedPatientsTab = ({ id, clientLinkMode = 'campaign' }: Props) => {
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState<number>(1);
  const [data, setData] = useState([]);
  const columns = useMemo(
    () => buildColumns(clientLinkMode),
    [clientLinkMode]
  );
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const { data: fetchedData, isLoading } = useQuery({
    queryKey: ['medical-professional-verified-patients', id, page],
    queryFn: () => fetchMedicalProfessionalPatients(id, 'verified', page),
  });

  const getPatients = () => {
    if (fetchedData) {
      const data = fetchedData?.results?.map((patient: any) => ({
        id: patient?.person?.id,
        patient_id: patient?.id,
        name: `${patient?.person?.first_name} ${patient?.person?.last_name}`,
        hippa: true,
        status: patient?.report_verification_status_code || 'UNVERIFIED',
        verifyExpensesStatus: 'Verified',
        verifyExpensesDate: patient?.verification_completion_date,
        verifyExpensesMedicalProfessional:
          patient?.medical_professional?.person?.first_name,
        // "Verified by" — first + last name of the assigned medical
        // professional from `MedicalProfessional.person`.
        verifierName: [
          patient?.medical_professional?.person?.first_name,
          patient?.medical_professional?.person?.last_name,
        ]
          .filter(Boolean)
          .join(' '),
        emailed: patient?.email_sent_to_medical_professional_date,
        campaign: patient?.campaign_ref,
      }));
      setData(data);
      const total = fetchedData?.count;
      if (total) {
        setPages(Math.ceil(total / 20));
      }
    }
  };

  useEffect(() => {
    getPatients();
  }, [fetchedData, isLoading]);

  return (
    <>
      <table className='datatable-table datatable-one w-full table-auto border-collapse overflow-hidden break-words px-4 md:table-fixed md:overflow-auto md:px-8'>
        <thead className='border-separate px-4'>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr
              className='border-t border-stroke bg-whiten'
              style={{ fontSize: '14px' }}
              key={headerGroup.id}
            >
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  style={{ paddingTop: '16px', paddingBottom: '16px' }}
                >
                  <div className='flex items-center'>
                    <span>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {isLoading
            ? Array.from({ length: 10 }).map((_, index) => (
                <tr key={index}>
                  {columns.map((_column, colIndex) => (
                    <td
                      key={colIndex}
                      className='border-b border-stroke px-4 py-2 dark:border-strokedark'
                    >
                      <div className='max-w-sm animate-pulse'>
                        <div className='h-2 max-w-[360px] rounded-full bg-gray-4 dark:bg-gray-5'></div>
                      </div>
                    </td>
                  ))}
                </tr>
              ))
            : table.getRowModel().rows.map((row) => (
                <tr className='border-t border-stroke' key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td
                      style={{ paddingTop: '16px', paddingBottom: '16px' }}
                      key={cell.id}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
        </tbody>
      </table>

      <Pagination page={page} pages={pages} setPage={setPage} totalCount={fetchedData?.count} />
    </>
  );
};

export default VerifiedPatientsTab;
